import React from "react";
import { Link } from "react-router-dom";

/**
 * Marketing/legal layout for apex-routed public pages on the platform
 * (clubs.clubcentral.app/sms-opt-in, /privacy, /terms, /privacy-policy,
 * /terms-of-service). Distinct from `<Navbar>` + `<Footer>`, which are
 * tenant-themed and read tenant settings — those don't make sense at
 * apex paths where there is no tenant context.
 *
 * Provides Club Central brand identity (logo, business name, copyright,
 * legal links) so Twilio reviewers, mobile carriers, and anyone hitting
 * a legal/disclosure URL sees a consistent, screenshot-ready brand.
 *
 * If/when a real marketing site ships at clubcentral.app (apex), the
 * styling here is intended to evolve toward whatever system that site
 * uses, so the platform's policy pages stay visually aligned.
 */

interface MarketingLayoutProps {
  children: React.ReactNode;
}

const MarketingLayout: React.FC<MarketingLayoutProps> = ({ children }) => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-900">
      {/* Branded top bar — Club Central marketing logo only; the page body
          (and footer copyright line) carry the legal-entity reference, so
          a "Club Central by MTG, LLC" text block alongside the logo would
          just be visual duplication. */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-4">
          <Link
            to="/sms-opt-in"
            aria-label="Club Central"
            className="flex items-center"
          >
            <img
              src="/club-central-marketing-b.png"
              alt="Club Central"
              className="h-20 sm:h-24 w-auto"
              width={320}
              height={96}
            />
          </Link>

          <nav className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm">
            <Link
              to="/sms-opt-in"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              SMS Info
            </Link>
            <Link
              to="/privacy"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Terms
            </Link>
          </nav>
        </div>
      </header>

      {/* Page content. Pages set their own inner max-width and padding so
          this layout stays neutral. */}
      <main className="flex-1">{children}</main>

      {/* Footer — logo only on the left, legal links + copyright + contact
          on the right. Same redundancy fix as the header: the copyright
          line below is the legal-entity reference, so a "Club Central /
          Operated by MTG, LLC" text block beside the logo would duplicate
          it. */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs text-gray-600">
          <img
            src="/club-central-marketing-b.png"
            alt="Club Central"
            className="h-14 w-auto opacity-80"
            width={200}
            height={56}
          />

          <div className="flex flex-col sm:items-end gap-1">
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-gray-900">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-gray-900">
                Terms of Service
              </Link>
              <Link to="/sms-opt-in" className="hover:text-gray-900">
                SMS Opt-In
              </Link>
            </div>
            <p>
              &copy; {currentYear} Media &amp; Technology Group, LLC. All rights
              reserved.
            </p>
            <p>
              <a
                href="mailto:support@clubcentral.app"
                className="hover:text-gray-900"
              >
                support@clubcentral.app
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;
